import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, X, Check, Copy, MapPin, Home, Building2, Hash, Globe2,
  Phone, MessageSquare, User, Truck, PackageCheck, Sprout, Ban,
  RotateCcw, ChevronRight, Boxes, CircleDollarSign, Clock,
  Leaf, Flame, WheatOff, Snowflake, AlertTriangle, Plus, ArrowRight,
} from "lucide-react";

import type { RootState, AppDispatch } from "../../../redux/store";
import {
  listenToFoodHostOrders,
  markFoodItemsDelivered,
  cancelFoodOrder,
  refundFoodOrder,
  FoodOrder,
} from "../../../redux/foodOrderSlice";
import { listenToPublishedFoods } from "../../../redux/foodSlice";
import { fetchAllUsers } from "../../../redux/authSlice";
import appLogo from "../../../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════
   DESIGN — a packing dock, not a shop.

   The buyer sees sunrise orange on cream. The grower works a
   different room: a chalkboard rail above a bench of kraft
   dockets. Deep forest ink, brown-paper stock, moss green for
   anything moving forward, tomato only where something is
   wrong. Mono numerals so order codes and weights line up
   the way they do on a real packing slip.
═══════════════════════════════════════════════════════════ */
const D = {
  board:     "#17231C",   // chalkboard
  boardSoft: "#22332A",
  kraft:     "#EFE0C8",   // brown paper
  kraftDeep: "#E3D0B2",
  paper:     "#FFFDF7",
  ink:       "#1B2A21",
  inkSoft:   "#3E5347",
  muted:     "#7E8F84",
  faint:     "#A6B3AA",
  moss:      "#2F6B3F",
  leaf:      "#58A65C",
  leafSoft:  "rgba(88,166,92,.12)",
  tomato:    "#D64541",
  tomatoSoft:"rgba(214,69,65,.10)",
  sun:       "#E9A21B",
  sunSoft:   "rgba(233,162,27,.14)",
  line:      "rgba(27,42,33,.12)",
  lineSoft:  "rgba(27,42,33,.07)",
};

const FALLBACK_IMG = appLogo;
const imgOr = (src?: string | null) => src || FALLBACK_IMG;

/* ═══════════════════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,700;12..96,800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,700&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{width:5px;height:5px;}
  ::-webkit-scrollbar-thumb{background:rgba(27,42,33,.18);border-radius:99px;}
  input,button,select{font-family:inherit;cursor:pointer;}
  .fh :focus-visible{outline:2px solid ${D.leaf};outline-offset:2px;border-radius:6px;}

  @keyframes fhIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
  @keyframes fhTick{0%,100%{transform:rotate(-1.5deg)}50%{transform:rotate(1.5deg)}}
  @keyframes fhGlow{0%,100%{opacity:.55}50%{opacity:1}}

  .fh{min-height:100vh;background:${D.kraft};font-family:'DM Sans',sans-serif;color:${D.ink};padding-bottom:40px;
      background-image:radial-gradient(rgba(27,42,33,.045) 1px, transparent 1px);background-size:15px 15px;}

  .fh-mono{font-family:'IBM Plex Mono',monospace;font-variant-numeric:tabular-nums;}
  .fh-disp{font-family:'Bricolage Grotesque','DM Sans',sans-serif;font-weight:800;letter-spacing:-.02em;}

  /* ── chalkboard rail ── */
  .fh-board{background:${D.board};color:#fff;position:sticky;top:0;z-index:120;
            box-shadow:0 10px 30px rgba(23,35,28,.28);}
  .fh-board::after{content:'';display:block;height:8px;
    background:repeating-linear-gradient(90deg, ${D.board} 0 22px, ${D.boardSoft} 22px 44px);}
  .fh-chalk{font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:rgba(255,255,255,.42);}

  .fh-stat{display:flex;flex-direction:column;gap:2px;padding:9px 15px;border-radius:12px;background:rgba(255,255,255,.06);
           border:1px solid rgba(255,255,255,.09);flex-shrink:0;min-width:96px;}
  .fh-stat b{font-family:'Bricolage Grotesque',sans-serif;font-size:22px;font-weight:800;line-height:1;color:#fff;}
  .fh-stat span{font-size:10px;color:rgba(255,255,255,.5);letter-spacing:.06em;}
  .fh-stat.hot b{color:${D.sun};}

  .fh-search{display:flex;align-items:center;gap:9px;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.14);
             border-radius:12px;padding:9px 13px;flex:1;min-width:180px;transition:border-color .18s;}
  .fh-search:focus-within{border-color:${D.leaf};}
  .fh-search input{flex:1;background:none;border:none;outline:none;color:#fff;font-size:13px;min-width:0;}
  .fh-search input::placeholder{color:rgba(255,255,255,.38);}

  .fh-lane-tab{background:none;border:none;padding:11px 2px;font-size:12px;font-weight:700;color:rgba(255,255,255,.45);
               position:relative;white-space:nowrap;display:flex;align-items:center;gap:7px;}
  .fh-lane-tab.on{color:#fff;}
  .fh-lane-tab.on::after{content:'';position:absolute;left:0;right:0;bottom:6px;height:2px;border-radius:2px;background:${D.leaf};}
  .fh-lane-tab .n{font-family:'IBM Plex Mono',monospace;font-size:11px;font-weight:700;padding:2px 7px;border-radius:999px;
                  background:rgba(255,255,255,.1);color:rgba(255,255,255,.75);}
  .fh-lane-tab.on .n{background:${D.leaf};color:${D.board};}

  /* ── the board bench ── */
  .fh-bench{display:grid;gap:16px;grid-template-columns:repeat(auto-fit,minmax(286px,1fr));align-items:start;}
  .fh-col-head{display:flex;align-items:center;gap:8px;padding:0 2px 10px;position:sticky;top:0;}
  .fh-col-rule{height:3px;border-radius:2px;margin-bottom:12px;}

  /* ── the docket: kraft slip with a punched spike hole and a tear line ── */
  .fh-dk{background:${D.paper};border:1px solid ${D.line};border-radius:14px;position:relative;
         box-shadow:0 2px 0 ${D.kraftDeep},0 10px 24px rgba(27,42,33,.09);animation:fhIn .28s ease both;margin-bottom:14px;overflow:hidden;}
  .fh-dk:hover{box-shadow:0 2px 0 ${D.kraftDeep},0 16px 34px rgba(27,42,33,.16);}
  .fh-dk-strip{height:6px;}
  .fh-dk-spike{position:absolute;top:16px;left:50%;transform:translateX(-50%);width:26px;height:7px;border-radius:99px;
               background:${D.kraftDeep};box-shadow:inset 0 1px 2px rgba(27,42,33,.35);}
  .fh-tear{position:relative;height:14px;}
  .fh-tear::before{content:'';position:absolute;left:12px;right:12px;top:50%;height:0;
    border-top:2px dashed ${D.line};}
  .fh-tear i{position:absolute;top:50%;width:14px;height:14px;border-radius:50%;background:${D.kraft};transform:translateY(-50%);}
  .fh-tear i.l{left:-7px;} .fh-tear i.r{right:-7px;}

  .fh-pip{flex:1;height:5px;border-radius:99px;background:${D.lineSoft};position:relative;overflow:hidden;}
  .fh-pip.on{background:${D.leaf};}
  .fh-pip.now{background:${D.leaf};animation:fhGlow 1.6s ease-in-out infinite;}
  .fh-pip.halt{background:${D.line};}

  .fh-chip{display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:700;padding:3px 9px;border-radius:999px;
           font-family:'DM Sans',sans-serif;white-space:nowrap;}

  .fh-btn{display:inline-flex;align-items:center;justify-content:center;gap:7px;padding:10px 15px;border-radius:11px;
          font-size:12.5px;font-weight:700;border:none;transition:transform .12s,background .16s,box-shadow .16s;font-family:'DM Sans',sans-serif;white-space:nowrap;}
  .fh-btn:active{transform:scale(.97);}
  .fh-btn.go{background:${D.moss};color:#fff;box-shadow:0 5px 14px rgba(47,107,63,.3);}
  .fh-btn.go:hover{background:#255736;}
  .fh-btn.go:disabled{background:${D.faint};box-shadow:none;cursor:not-allowed;}
  .fh-btn.line{background:${D.paper};color:${D.ink};border:1px solid ${D.line};}
  .fh-btn.line:hover{background:${D.kraft};}
  .fh-btn.warn{background:${D.tomatoSoft};color:${D.tomato};border:1px solid rgba(214,69,65,.25);}
  .fh-btn.warn:hover{background:rgba(214,69,65,.18);}

  .fh-item{display:flex;gap:10px;padding:10px 0;border-bottom:1px dashed ${D.line};}
  .fh-item:last-child{border-bottom:none;}
  .fh-th{width:46px;height:46px;border-radius:10px;overflow:hidden;flex-shrink:0;background:${D.kraft};
         display:flex;align-items:center;justify-content:center;border:1px solid ${D.line};}

  /* ── address sheet ── */
  .fh-ov{position:fixed;inset:0;background:rgba(23,35,28,.55);backdrop-filter:blur(3px);z-index:400;display:flex;align-items:flex-end;justify-content:center;}
  @media(min-width:768px){.fh-ov{align-items:center;}}
  .fh-sheet{background:${D.paper};width:100%;max-width:440px;border-radius:20px 20px 0 0;max-height:86vh;overflow-y:auto;position:relative;
            box-shadow:0 -14px 44px rgba(23,35,28,.3);}
  @media(min-width:768px){.fh-sheet{border-radius:20px;margin:0 16px;}}
  .fh-grab{width:40px;height:4px;border-radius:99px;background:${D.line};margin:10px auto 2px;}
  .fh-arow{display:flex;gap:12px;align-items:flex-start;padding:11px 0;border-bottom:1px dashed ${D.line};}
  .fh-arow:last-child{border-bottom:none;}
  .fh-cp{display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:9px;flex-shrink:0;
         background:${D.kraft};border:1px solid ${D.line};transition:border-color .15s;}
  .fh-cp:hover{border-color:${D.leaf};}

  @media (prefers-reduced-motion: reduce){
    .fh-dk,.fh-pip.now{animation:none !important;}
  }
`;

/* ─── helpers ─── */
const money = (n?: number) => `€${(!n || isNaN(n) ? 0 : Number(n)).toFixed(2)}`;
const fmtD = (ts: any) => {
  if (!ts) return "—";
  const ms = ts?.seconds ? ts.seconds * 1000 : ts;
  const d = new Date(ms);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};
const fmtAgo = (ts: any) => {
  if (!ts) return "";
  const ms = ts?.seconds ? ts.seconds * 1000 : ts;
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (isNaN(mins) || mins < 0) return "";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

interface PortionRef { id: string; name: string; unit?: string }
interface ExtraRef { id: string; name: string }

const portionsOf = (listing: any): PortionRef[] => {
  const raw = listing?.portions;
  if (!raw) return [];
  return Array.isArray(raw)
    ? raw.map((p: any) => ({ id: String(p?.id ?? ""), name: p?.name ?? "", unit: p?.unit ?? "" }))
    : Object.entries(raw).map(([k, v]: [string, any]) => ({ id: k, name: v?.name ?? k, unit: v?.unit ?? "" }));
};
const extrasOf = (listing: any): ExtraRef[] =>
  Array.isArray(listing?.extras) ? listing.extras.map((e: any) => ({ id: String(e?.id ?? ""), name: e?.name ?? "" })) : [];

/* Only the lines this grower is responsible for. */
const hostItemsOf = (o: FoodOrder, hostId: string) =>
  (o.items ?? []).filter((i: any) => i.hostId === hostId);

/* This grower's cut, read from the per-host map the order carries. */
const hostMoneyOf = (o: FoodOrder, hostId: string) =>
  o.hostProfits?.[hostId] ?? { saleTotal: 0, hostsFee: 0, deliveryFee: 0, hostProfit: 0 };

/* ═══════════════════════════════════════════════════════════
   LANES — where a docket sits on the bench
═══════════════════════════════════════════════════════════ */
type LaneKey = "pack" | "road" | "done" | "closed";

const LANES: { key: LaneKey; label: string; rule: string; tint: string; Icon: any }[] = [
  { key: "pack",   label: "To pack",     rule: D.sun,    tint: D.sunSoft,    Icon: Boxes        },
  { key: "road",   label: "On the road", rule: D.moss,   tint: D.leafSoft,   Icon: Truck        },
  { key: "done",   label: "Delivered",   rule: D.leaf,   tint: D.leafSoft,   Icon: PackageCheck },
  { key: "closed", label: "Closed",      rule: D.tomato, tint: D.tomatoSoft, Icon: Ban          },
];

/* This grower's own rollup — every line they cook must carry the flag.
   Legacy docs with no per-line status fall back to the order's. */
const hostDS = (o: FoodOrder, hostId: string) => {
  const lines = hostItemsOf(o, hostId);
  if (!lines.length) return o.deliveryStatus ?? { sellerDelivered: false, orderDelivered: false, buyerReceived: false };
  const every = (k: "sellerDelivered" | "orderDelivered" | "buyerReceived") =>
    lines.every((i: any) => (i.deliveryStatus ?? o.deliveryStatus ?? {})[k] === true);
  return {
    sellerDelivered: every("sellerDelivered"),
    orderDelivered: every("orderDelivered"),
    buyerReceived: every("buyerReceived"),
  };
};

const laneOf = (o: FoodOrder, hostId: string): LaneKey => {
  if (o.isCancelled || o.isRefunded) return "closed";
  const d = hostDS(o, hostId);
  if (d.buyerReceived) return "done";
  if (d.orderDelivered) return "road";
  return "pack";
};

/* four beads: paid → packed → out → delivered */
const beadsOf = (o: FoodOrder, hostId: string) => {
  const halt = !!(o.isCancelled || o.isRefunded);
  const d = hostDS(o, hostId);
  const done = [!!o.isPaid, d.sellerDelivered, d.orderDelivered, d.buyerReceived];
  return { halt, done, at: done.lastIndexOf(true) + 1 };
};

/* ═══════════════════════════════════════════════════════════
   ADDRESS SHEET
═══════════════════════════════════════════════════════════ */
const AddrLine: React.FC<{ icon: React.ElementType; label: string; value: string }> = ({ icon: Icon, label, value }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch {}
  };
  if (!value) return null;
  return (
    <div className="fh-arow">
      <div style={{ width: 34, height: 34, borderRadius: 10, background: D.kraft, border: `1px solid ${D.line}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={15} color={D.moss} strokeWidth={1.8} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="fh-mono" style={{ fontSize: 9, letterSpacing: ".16em", color: D.muted }}>{label.toUpperCase()}</div>
        <div style={{ fontSize: 13, fontWeight: 500, color: D.ink, marginTop: 2, wordBreak: "break-word" as const }}>{value}</div>
      </div>
      <button className="fh-cp" onClick={copy} aria-label={`Copy ${label}`}>
        {copied ? <Check size={13} color={D.leaf} /> : <Copy size={13} color={D.muted} />}
      </button>
    </div>
  );
};

const AddressSheet: React.FC<{ order: FoodOrder; guest: any; guestName: string; onClose: () => void }> = ({ order, guest, guestName, onClose }) => {
  const addr = guest?.shippingAddress || {};
  const c = order.customer;
  const street = addr.streetAddress || c?.address || "";
  const cityLine = [addr.city || c?.city, addr.state].filter(Boolean).join(", ");
  const countryLine = [addr.zipCode, addr.country || c?.country].filter(Boolean).join(", ");
  const phone = c?.phone || guest?.contactNumber || guest?.phoneNumber || "";
  const note = addr.shippingMessage?.trim() || "No delivery note";
  const hasAny = street || cityLine || countryLine || phone;

  return (
    <motion.div className="fh-ov" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div className="fh-sheet" initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ duration: .26, ease: [.4, 0, .2, 1] }} onClick={e => e.stopPropagation()}>
        <div className="fh-grab" />
        <div style={{ padding: "14px 20px 24px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 12, background: D.moss, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <MapPin size={17} color="#fff" />
              </div>
              <div>
                <div className="fh-disp" style={{ fontSize: 16, color: D.ink }}>Drop-off</div>
                <div style={{ fontSize: 11.5, color: D.muted, marginTop: 1 }}>{guestName}</div>
              </div>
            </div>
            <button onClick={onClose} aria-label="Close"
              style={{ width: 30, height: 30, borderRadius: 10, border: `1px solid ${D.line}`, background: D.kraft, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <X size={14} color={D.muted} />
            </button>
          </div>

          {hasAny ? (
            <div style={{ background: D.kraft, border: `1px solid ${D.line}`, borderRadius: 14, padding: "4px 14px" }}>
              <AddrLine icon={Home} label="Street" value={street} />
              <AddrLine icon={Building2} label="Apartment / suite" value={addr.apartmentNumber} />
              <AddrLine icon={Hash} label="P.O. box" value={addr.postalAddress} />
              <AddrLine icon={MapPin} label="City / region" value={cityLine} />
              <AddrLine icon={Globe2} label="Post code / country" value={countryLine} />
              <AddrLine icon={Phone} label="Phone" value={phone || "Not provided"} />
              <AddrLine icon={MessageSquare} label="Delivery note" value={note} />
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "28px 12px", color: D.muted, fontSize: 13 }}>
              <MapPin size={26} color={D.faint} style={{ marginBottom: 8 }} />
              <div>No address on this order yet.</div>
            </div>
          )}

          <button className="fh-btn line" style={{ width: "100%", marginTop: 16 }} onClick={onClose}>Close</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════
   DOCKET
═══════════════════════════════════════════════════════════ */
const Docket: React.FC<{
  order: FoodOrder; hostId: string; guestName: string; listingsMap: Record<string, any>;
  onPacked: (o: FoodOrder) => void; onOut: (o: FoodOrder) => void; onDelivered: (o: FoodOrder) => void;
  onCancel: (o: FoodOrder) => void; onRefund: (o: FoodOrder) => void; onAddress: (o: FoodOrder) => void;
}> = ({ order, hostId, guestName, listingsMap, onPacked, onOut, onDelivered, onCancel, onRefund, onAddress }) => {
  const [open, setOpen] = useState(false);
  const items = hostItemsOf(order, hostId);
  const cash = hostMoneyOf(order, hostId);
  const lane = laneOf(order, hostId);
  const { halt, done, at } = beadsOf(order, hostId);
  const code = (order.paymentId || order.id || "").slice(-8).toUpperCase();
  const unpaid = !order.isPaid && !halt;
  const packed = hostDS(order, hostId).sellerDelivered;
  const rule = LANES.find(l => l.key === lane)!.rule;
  const qty = items.reduce((s: number, i: any) => s + (i.qty ?? 0), 0);

  /* the one action this docket is waiting on */
  const primary =
    halt ? null
    : lane === "done" ? null
    : !packed ? { label: "Mark packed", Icon: Boxes, fn: () => onPacked(order), off: unpaid }
    : lane === "pack" ? { label: "Hand to courier", Icon: Truck, fn: () => onOut(order), off: false }
    : { label: "Mark delivered", Icon: PackageCheck, fn: () => onDelivered(order), off: false };

  return (
    <div className="fh-dk">
      <div className="fh-dk-strip" style={{ background: rule }} />
      <div className="fh-dk-spike" />

      {/* head */}
      <div style={{ padding: "18px 15px 12px", cursor: "pointer" }} onClick={() => setOpen(v => !v)}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div className="fh-mono" style={{ fontSize: 15, fontWeight: 700, color: D.ink, letterSpacing: ".04em" }}>#{code}</div>
            <div style={{ fontSize: 11, color: D.muted, marginTop: 3, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><User size={10} /> {guestName}</span>
              <span>·</span>
              <span>{fmtD(order.createdAt)}</span>
              {fmtAgo(order.createdAt) && <span style={{ color: D.faint }}>{fmtAgo(order.createdAt)}</span>}
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div className="fh-mono fh-disp" style={{ fontSize: 19, color: D.moss }}>{money(cash.hostProfit)}</div>
            <div style={{ fontSize: 9.5, color: D.faint, letterSpacing: ".06em" }}>YOUR PROFIT</div>
          </div>
        </div>

        {/* flags */}
        <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
          <span className="fh-chip" style={{ background: D.kraft, color: D.inkSoft }}>
            <Boxes size={10} /> {items.length} line{items.length !== 1 ? "s" : ""} · {qty} pc
          </span>
          {unpaid && <span className="fh-chip" style={{ background: D.sunSoft, color: "#9A6A00" }}><AlertTriangle size={10} /> Unpaid</span>}
          {packed && !halt && <span className="fh-chip" style={{ background: D.leafSoft, color: D.moss }}><Check size={10} /> Packed</span>}
          {order.isRefunded && <span className="fh-chip" style={{ background: D.tomatoSoft, color: D.tomato }}><RotateCcw size={10} /> Refunded</span>}
          {order.isCancelled && <span className="fh-chip" style={{ background: D.tomatoSoft, color: D.tomato }}><Ban size={10} /> Cancelled</span>}
          {order.fulfillmentMethod && <span className="fh-chip" style={{ background: D.kraft, color: D.muted }}>{order.fulfillmentMethod}</span>}
        </div>

        {/* four beads: paid → packed → out → delivered */}
        <div style={{ display: "flex", gap: 5, marginTop: 12 }}>
          {done.map((d, i) => (
            <div key={i} className={`fh-pip${halt ? (d ? " halt" : "") : d ? (i === at - 1 && at < 4 ? " now" : " on") : ""}`} />
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
          <span className="fh-mono" style={{ fontSize: 9, color: D.faint, letterSpacing: ".1em" }}>
            {halt ? "STOPPED" : ["AWAITING PAYMENT", "PAID", "PACKED", "OUT FOR DELIVERY", "DELIVERED"][at]}
          </span>
          <ChevronRight size={14} color={D.faint} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }} />
        </div>
      </div>

      {/* primary action always visible — the bench shouldn't need a tap to work */}
      <div style={{ padding: "0 15px 14px", display: "flex", gap: 8, flexWrap: "wrap" }}>
        {primary && (
          <button className="fh-btn go" style={{ flex: 1, minWidth: 150 }} disabled={primary.off} onClick={primary.fn}
            title={primary.off ? "Payment hasn't cleared yet" : undefined}>
            <primary.Icon size={14} /> {primary.label}
          </button>
        )}
        <button className="fh-btn line" onClick={() => onAddress(order)}>
          <MapPin size={13} /> Drop-off
        </button>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: .26, ease: [.4, 0, .2, 1] }} style={{ overflow: "hidden" }} onClick={e => e.stopPropagation()}>
            <div className="fh-tear"><i className="l" /><i className="r" /></div>

            <div style={{ padding: "4px 15px 16px", background: "rgba(239,224,200,.35)" }}>
              <div className="fh-mono" style={{ fontSize: 9, letterSpacing: ".2em", color: D.muted, marginBottom: 6 }}>PICK LIST</div>

              {items.map((item: any, i: number) => {
                const p = listingsMap[item.listingId] ?? {};
                const img = imgOr(p?.photos?.[0] || item.photo);
                const isFb = img === FALLBACK_IMG;
                const size = item.portionId ? portionsOf(p).find(x => x.id === item.portionId) : null;
                const extraNames = (item.extraIds ?? [])
                  .map((id: string) => extrasOf(p).find(e => e.id === id)?.name).filter(Boolean) as string[];
                const measure = p?.netContent && p?.unit ? `${p.netContent} ${p.unit}` : p?.unit || "";
                const storage = String(p?.storage ?? "").toLowerCase();

                return (
                  <div className="fh-item" key={(item.listingId ?? i) + "-" + i}>
                    <div className="fh-th">
                      <img src={img} alt={p?.name || ""}
                        style={isFb ? { width: "58%", height: "58%", objectFit: "contain", opacity: .5 } : { width: "100%", height: "100%", objectFit: "cover" }}
                        onError={e => { const t = e.currentTarget as HTMLImageElement; t.src = FALLBACK_IMG; t.style.width = "58%"; t.style.height = "58%"; t.style.objectFit = "contain"; t.style.opacity = ".5"; }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: D.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p?.name || item.name || "Item"}
                      </div>
                      <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                        {size && <span className="fh-chip" style={{ background: D.sunSoft, color: "#9A6A00" }}>{size.name}{size.unit ? ` · ${size.unit}` : ""}</span>}
                        {measure && <span className="fh-chip" style={{ background: D.kraft, color: D.muted }}>{measure}</span>}
                        {extraNames.map((n, k) => <span key={k} className="fh-chip" style={{ background: D.kraft, color: D.inkSoft }}>+ {n}</span>)}
                        {p?.isOrganic && <span className="fh-chip" style={{ background: D.leafSoft, color: D.moss }}><Leaf size={9} /> Organic</span>}
                        {p?.isGlutenFree && <span className="fh-chip" style={{ background: D.sunSoft, color: "#9A6A00" }}><WheatOff size={9} /> GF</span>}
                        {(p?.spiceLevel ?? 0) > 0 && <span className="fh-chip" style={{ background: D.tomatoSoft, color: D.tomato }}><Flame size={9} /> Spicy</span>}
                        {storage && <span className="fh-chip" style={{ background: D.kraft, color: D.muted }}><Snowflake size={9} /> {storage}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div className="fh-mono" style={{ fontSize: 15, fontWeight: 700, color: D.ink }}>×{item.qty}</div>
                      <div className="fh-mono" style={{ fontSize: 11, color: D.muted, marginTop: 2 }}>
                        {money(item.saleTotal ?? (item.rate ?? 0) * (item.rateType === "perProduct" ? 1 : (item.qty ?? 1)))}
                      </div>
                    </div>
                  </div>
                );
              })}

              {items.length === 0 && (
                <div style={{ padding: "14px 0", fontSize: 12, color: D.muted }}>No lines on this order belong to you.</div>
              )}

              {/* your cut */}
              <div style={{ marginTop: 14, background: D.paper, border: `1px solid ${D.line}`, borderRadius: 12, padding: "12px 14px" }}>
                <div className="fh-mono" style={{ fontSize: 9, letterSpacing: ".2em", color: D.muted, marginBottom: 8 }}>YOUR CUT</div>
                {[
                  { l: "Sale total", v: money(cash.saleTotal) },
                  { l: "Platform fee", v: `− ${money(cash.hostsFee)}` },
                  { l: "Delivery", v: cash.deliveryFee ? money(cash.deliveryFee) : "Free" },
                ].map((r, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px dashed ${D.line}` }}>
                    <span style={{ fontSize: 12, color: D.muted }}>{r.l}</span>
                    <span className="fh-mono" style={{ fontSize: 12, fontWeight: 600, color: D.inkSoft }}>{r.v}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 9 }}>
                  <span className="fh-disp" style={{ fontSize: 13, color: D.ink }}>You keep</span>
                  <span className="fh-mono fh-disp" style={{ fontSize: 18, color: D.moss }}>{money(cash.hostProfit)}</span>
                </div>
                {order.isProcessedForPayout && (
                  <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: D.moss }}>
                    <CircleDollarSign size={12} /> Paid out
                  </div>
                )}
              </div>

              {/* secondary actions */}
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                {!halt && lane !== "done" && (
                  <button className="fh-btn warn" onClick={() => onCancel(order)}>
                    <Ban size={12} /> Cancel order
                  </button>
                )}
                {order.isPaid && !order.isRefunded && (
                  <button className="fh-btn line" onClick={() => onRefund(order)}>
                    <RotateCcw size={12} /> Refund
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
interface Props { brandId?: string }

const FreshHostOrders: React.FC<Props> = ({ brandId }) => {
  const { userId } = useParams<{ userId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const authUser = useSelector((s: RootState) => s.auth.user);
  const hostId = brandId ?? userId ?? authUser?.userId ?? "";

  const orders = useSelector((s: RootState) => (s as any).FoodOrder?.hostOrders ?? []);
  const listings = useSelector((s: RootState) => (s as any).food?.listings ?? []);
  const allUsers = useSelector((s: RootState) => (s.auth as any).allUsers ?? []);

  const [search, setSearch] = useState("");
  const [lane, setLane] = useState<LaneKey | "all">("all");
  const [isMob, setIsMob] = useState(window.innerWidth < 900);
  const [addressOrder, setAddressOrder] = useState<FoodOrder | null>(null);

  useEffect(() => { const fn = () => setIsMob(window.innerWidth < 900); window.addEventListener("resize", fn); return () => window.removeEventListener("resize", fn); }, []);
  useEffect(() => { if (isMob && lane === "all") setLane("pack"); }, [isMob]); // eslint-disable-line

  useEffect(() => {
    if (!hostId) return;
    const unsub = dispatch(listenToFoodHostOrders(hostId) as any);
    dispatch(listenToPublishedFoods() as any);
    dispatch(fetchAllUsers());
    return () => { if (typeof unsub === "function") unsub(); };
  }, [hostId, dispatch]);

  const listingsMap = useMemo(() => {
    const m: Record<string, any> = {};
    (listings as any[]).forEach((l: any) => { m[l.listingId] = l; });
    return m;
  }, [listings]);

  const findGuest = (id: string) => (allUsers as any[]).find(u => u.userId === id || u.uid === id) || null;
  const guestName = (o: FoodOrder) => {
    const u = findGuest(o.guestId);
    const fromOrder = [o.customer?.firstName, o.customer?.lastName].filter(Boolean).join(" ").trim();
    return fromOrder || u?.firstName || u?.displayName || u?.email || "Customer";
  };

  const sorted = useMemo(
    () => [...(orders as FoodOrder[])].sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0)),
    [orders]
  );

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(o => {
      const names = hostItemsOf(o, hostId)
        .map((i: any) => listingsMap[i.listingId]?.name ?? i.name ?? "").join(" ");
      return `${o.paymentId ?? ""} ${o.id ?? ""} ${names} ${guestName(o)}`.toLowerCase().includes(q);
    });
  }, [sorted, search, listingsMap, allUsers, hostId]);

  const byLane = useMemo(() => {
    const m: Record<LaneKey, FoodOrder[]> = { pack: [], road: [], done: [], closed: [] };
    searched.forEach(o => m[laneOf(o, hostId)].push(o));
    return m;
  }, [searched]);

  /* bench totals — what's owed and what's waiting */
  const stats = useMemo(() => {
    const open = [...byLane.pack, ...byLane.road];
    const owed = open.reduce((s, o) => s + (hostMoneyOf(o, hostId).hostProfit ?? 0), 0);
    const earned = byLane.done.reduce((s, o) => s + (hostMoneyOf(o, hostId).hostProfit ?? 0), 0);
    const pieces = byLane.pack.reduce((s, o) => s + hostItemsOf(o, hostId).reduce((n: number, i: any) => n + (i.qty ?? 0), 0), 0);
    return { toPack: byLane.pack.length, onRoad: byLane.road.length, owed, earned, pieces };
  }, [byLane, hostId]);

  /* Stamps only this grower's own lines — the food twin of
     markStoreItemsDelivered({ brandIds, role:"seller" }). On a
     multi-kitchen order, one grower packing can't close the rest. */
  const markMine = (o: FoodOrder) =>
    dispatch(markFoodItemsDelivered({ orderId: o.id, brandIds: [hostId], role: "seller" }) as any);

  const onPacked = markMine;
  const onOut = markMine;
  const onDelivered = markMine;
  const onCancel = (o: FoodOrder) => dispatch(cancelFoodOrder(o.id) as any);
  const onRefund = (o: FoodOrder) => dispatch(refundFoodOrder(o.id) as any);

  const visibleLanes: LaneKey[] = lane === "all" ? LANES.map(l => l.key) : [lane];

  /* ── empty bench ── */
  if (sorted.length === 0) return (
    <div className="fh" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 28 }}>
      <style>{CSS}</style>
      <div style={{ textAlign: "center", maxWidth: 360 }}>
        <div style={{ width: 84, height: 84, borderRadius: 24, background: D.board, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 22px", boxShadow: "0 14px 34px rgba(23,35,28,.28)" }}>
          <Sprout size={34} color={D.leaf} />
        </div>
        <h2 className="fh-disp" style={{ fontSize: 26, color: D.ink, marginBottom: 8 }}>The bench is empty</h2>
        <p style={{ color: D.muted, fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
          Orders for your produce land here as dockets, ready to pack.
        </p>
        <button className="fh-btn go" onClick={() => navigate(`/store/${hostId}/fresh/new`)}>
          <Plus size={14} /> Add an item <ArrowRight size={13} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="fh">
      <style>{CSS}</style>

      {/* ── chalkboard rail ── */}
      <div className="fh-board">
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "16px 20px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
            <div style={{ width: 34, height: 34, borderRadius: 11, overflow: "hidden", flexShrink: 0, background: "rgba(255,255,255,.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src={appLogo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <div style={{ marginRight: "auto" }}>
              <div className="fh-chalk">Packing dock</div>
              <h1 className="fh-disp" style={{ fontSize: isMob ? 22 : 27, color: "#fff", lineHeight: 1.1 }}>Today's dockets</h1>
            </div>
            <div className="fh-search">
              <Search size={14} color="rgba(255,255,255,.45)" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search code, produce or customer…" aria-label="Search orders" />
              {search && (
                <button onClick={() => setSearch("")} aria-label="Clear search" style={{ background: "none", border: "none", display: "flex" }}>
                  <X size={13} color="rgba(255,255,255,.5)" />
                </button>
              )}
            </div>
          </div>

          {/* the numbers that decide the day */}
          <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 14, scrollbarWidth: "none" as any }}>
            <div className={`fh-stat${stats.toPack ? " hot" : ""}`}><b className="fh-mono">{stats.toPack}</b><span>to pack</span></div>
            <div className="fh-stat"><b className="fh-mono">{stats.pieces}</b><span>pieces</span></div>
            <div className="fh-stat"><b className="fh-mono">{stats.onRoad}</b><span>on the road</span></div>
            <div className="fh-stat"><b className="fh-mono">{money(stats.owed)}</b><span>in flight</span></div>
            <div className="fh-stat"><b className="fh-mono">{money(stats.earned)}</b><span>delivered</span></div>
          </div>

          {/* lanes */}
          <div style={{ display: "flex", gap: 20, overflowX: "auto", scrollbarWidth: "none" as any, borderTop: "1px solid rgba(255,255,255,.08)" }}>
            {!isMob && (
              <button className={`fh-lane-tab${lane === "all" ? " on" : ""}`} onClick={() => setLane("all")}>
                Whole bench <span className="n">{searched.length}</span>
              </button>
            )}
            {LANES.map(l => (
              <button key={l.key} className={`fh-lane-tab${lane === l.key ? " on" : ""}`} onClick={() => setLane(l.key)}>
                <l.Icon size={13} /> {l.label} <span className="n">{byLane[l.key].length}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── the bench ── */}
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: isMob ? "18px 14px" : "24px 20px" }}>
        <div className="fh-bench" style={lane !== "all" ? { gridTemplateColumns: isMob ? "1fr" : "repeat(auto-fit,minmax(300px,1fr))" } : undefined}>
          {visibleLanes.map(key => {
            const meta = LANES.find(l => l.key === key)!;
            const list = byLane[key];
            return (
              <div key={key}>
                <div className="fh-col-head">
                  <meta.Icon size={14} color={meta.rule} />
                  <span className="fh-disp" style={{ fontSize: 13.5, color: D.ink }}>{meta.label}</span>
                  <span className="fh-mono" style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: D.muted, background: meta.tint, padding: "2px 9px", borderRadius: 999 }}>
                    {list.length}
                  </span>
                </div>
                <div className="fh-col-rule" style={{ background: meta.rule, opacity: .35 }} />

                {list.length === 0 ? (
                  <div style={{ border: `1.5px dashed ${D.line}`, borderRadius: 14, padding: "26px 14px", textAlign: "center", color: D.faint, fontSize: 12 }}>
                    {key === "pack" ? "Nothing waiting. Nice." : key === "road" ? "No runs out right now." : key === "done" ? "Nothing delivered yet." : "Nothing closed."}
                  </div>
                ) : list.map(o => (
                  <Docket key={o.id} order={o} hostId={hostId} guestName={guestName(o)} listingsMap={listingsMap}
                    onPacked={onPacked} onOut={onOut} onDelivered={onDelivered}
                    onCancel={onCancel} onRefund={onRefund} onAddress={setAddressOrder} />
                ))}
              </div>
            );
          })}
        </div>

        {searched.length === 0 && search && (
          <div style={{ textAlign: "center", padding: "50px 20px" }}>
            <Clock size={30} color={D.faint} style={{ marginBottom: 10 }} />
            <h3 className="fh-disp" style={{ fontSize: 17, color: D.ink, marginBottom: 6 }}>Nothing matches “{search}”</h3>
            <p style={{ fontSize: 13, color: D.muted }}>Try the order code, a customer name, or the produce.</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {addressOrder && (
          <AddressSheet order={addressOrder} guest={findGuest(addressOrder.guestId)}
            guestName={guestName(addressOrder)} onClose={() => setAddressOrder(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default FreshHostOrders;